import React from "react";

import { useTranslation } from "utils";

import { Flex, Icon, Tooltip, Typography } from "components";

export type PracticeAssessmentData = {
  year: number;
  totalHa: number;
  totalActualHa: number;
  practices: {
    key: string;
    label: string;
    tooltip?: string;
    actual: number | null;
    target: number;
  }[];
  missingFieldsCount: number;
  totalFieldsCount: number;
};

type PracticeAssessmentProps = {
  data: PracticeAssessmentData;
};

export const PracticeAssessment = ({ data }: PracticeAssessmentProps) => {
  const { t } = useTranslation("fieldTimeline");
  const { year, totalHa, totalActualHa, practices, missingFieldsCount, totalFieldsCount } = data;

  const totalPercentage = Math.min(totalHa > 0 ? Math.round((totalActualHa / totalHa) * 100) : 0, 100);

  return (
    <div className="practice-assessment">
      <Flex align="center" justify="space-between" className="practice-assessment-header">
        <Flex align="center" gap="0.375rem">
          <Typography variant="paragraph-m" bold className="practice-assessment-title">
            {t("impactPanel.practiceAssessment.title", "PRACTICE ASSESSMENT")}
          </Typography>
          <Tooltip
            title={t(
              "impactPanel.practiceAssessment.tooltip",
              "Shows how many hectares of your farm are under regenerative practices — compared to your programme targets."
            )}
          >
            <span>
              <Icon iconName="tabler/info-circle" size="xs" className="practice-assessment-info-icon" />
            </span>
          </Tooltip>
        </Flex>
        <span className="practice-assessment-year-badge">{year}</span>
      </Flex>

      <Flex align="baseline" gap="0.25rem" className="practice-assessment-total-row">
        <Typography variant="h3" className="practice-assessment-total-actual">
          {totalActualHa} ha
        </Typography>
        <Typography variant="paragraph-s" className="practice-assessment-total-target">
          {t("impactPanel.practiceAssessment.totalHa", "/ {{totalHa}} ha total", { totalHa })}
        </Typography>
      </Flex>

      <Typography variant="paragraph-xs" className="practice-assessment-subtitle">
        {t("impactPanel.practiceAssessment.subtitle", "{{percentage}}% of ha with practices", { percentage: totalPercentage })}
      </Typography>

      <div className="practice-assessment-progress-bar">
        <div className="practice-assessment-progress-bar-fill" style={{ width: `${totalPercentage}%` }} />
      </div>

      <Flex vertical gap="0" className="practice-assessment-rows">
        {practices.map((p) => {
          const percentage = p.actual !== null && p.target > 0 ? Math.min(Math.round((p.actual / p.target) * 100), 100) : null;
          return (
            <div key={p.key} className="practice-assessment-row">
              <Flex align="center" justify="space-between">
                <Flex align="center" gap="0.25rem">
                  <Typography variant="paragraph-s" bold className="practice-assessment-row-label">
                    {p.label}
                  </Typography>
                  {p.tooltip && (
                    <Tooltip title={p.tooltip}>
                      <span>
                        <Icon iconName="tabler/info-circle" size="xs" className="practice-assessment-row-info-icon" />
                      </span>
                    </Tooltip>
                  )}
                </Flex>
                <Typography
                  variant="paragraph-s"
                  className={
                    p.actual !== null
                      ? "practice-assessment-row-ha practice-assessment-row-ha--filled"
                      : "practice-assessment-row-ha practice-assessment-row-ha--empty"
                  }
                >
                  {p.actual !== null ? `${p.actual} / ${p.target} ha` : `— / ${p.target} ha`}
                </Typography>
              </Flex>
              <div className="practice-assessment-row-progress">
                <div className="practice-assessment-row-progress-fill" style={{ width: `${percentage ?? 0}%` }} />
              </div>
              {percentage !== null && (
                <Typography variant="paragraph-xs" className="practice-assessment-row-percentage">
                  {t("impactPanel.practiceAssessment.percentageComplete", "{{percentage}}% complete", { percentage })}
                </Typography>
              )}
            </div>
          );
        })}
      </Flex>

      {missingFieldsCount > 0 && (
        <Typography variant="paragraph-xs" className="practice-assessment-footer">
          {t(
            "impactPanel.practiceAssessment.missingFieldsNote",
            "{{missing}}/{{total}} fields are not contributing to the assessment yet because of missing data.",
            {
              missing: missingFieldsCount,
              total: totalFieldsCount,
            }
          )}
        </Typography>
      )}
    </div>
  );
};
